import { inject, Injectable, signal } from '@angular/core';

import {
  HttpClient,
  HttpHeaders
} from '@angular/common/http';

import {
  catchError,
  finalize,
  Observable,
  tap,
  throwError
} from 'rxjs';

// ======================================================
// SERVICES
// ======================================================

import { TokenService } from '../services/token.service';
import { LoaderService } from '../services/loader.service';
import { CookieService } from '../services/cookie.service';
import { AlertService } from '../services/alert.service';

// ======================================================
// MODELS / ENUMS
// ======================================================


import { Name } from '../enums/name.enum';
import { ResponseCode } from '../enums/responsecode.enum';

// ======================================================
// CONSTANTS
// ======================================================

import { APIConfig } from '../models/api-config.model';
import { GET_MEDIA_TOKEN_URL } from '../constants/apiEndpoints.constants';

// ======================================================
// TYPES
// ======================================================

type HeadersType =
  | HttpHeaders
  | Record<string, string>;

// ======================================================
// DEFAULT CONFIG
// ======================================================

const DEFAULT_CONFIG: APIConfig = {
  loadInBackground: false,
  isTesterFlow: false
};

@Injectable({
  providedIn: 'root'
})
export class HttpService {

  // ======================================================
  // DI
  // ======================================================

  private readonly http = inject(HttpClient);

  private readonly cookieSrv = inject(CookieService);

  private readonly tokenSrv = inject(TokenService);

  private readonly loaderSrv = inject(LoaderService);

  private readonly alertSrv = inject(AlertService);

  // ======================================================
  // STATE
  // ======================================================

  readonly isLoading = signal(false);

  private readonly mediaTokens = signal<
    Record<
      string,
      {
        token: string;
        expiryTime?: string | null;
      }
    >
  >({});

  private checkedStorage = false;

  // ======================================================
  // GET
  // ======================================================

  get<T>(
    url: string,
    headers: HeadersType = {},
    config: APIConfig = DEFAULT_CONFIG
  ): Observable<T> {

    return this.http.get<T>(
      url,
      {
        headers: this.buildHeaders(headers),
        params: config.queryParams ?? {}
      }
    ).pipe(
      this.handleRequest(config)
    );
  }

  // ======================================================
  // GET BLOB
  // ======================================================

  getBlob(
    url: string,
    headers: HeadersType = {},
    config: APIConfig = DEFAULT_CONFIG
  ) {

    return this.http.get(
      url,
      {
        headers: this.buildHeaders(headers),
        responseType: 'blob'
      }
    ).pipe(
      this.handleRequest(config)
    );
  }

  // ======================================================
  // POST
  // ======================================================

  post<T>(
    url: string,
    payload: unknown,
    headers: HeadersType = {},
    config: APIConfig = DEFAULT_CONFIG
  ): Observable<T> {

    return this.http.post<T>(
      url,
      payload,
      {
        headers: this.buildHeaders(
          headers,
          true
        )
      }
    ).pipe(
      this.handleRequest(config)
    );
  }

  // ======================================================
  // PUT
  // ======================================================

  put<T>(
    url: string,
    payload: unknown,
    headers: HeadersType = {},
    config: APIConfig = DEFAULT_CONFIG
  ): Observable<T> {

    return this.http.put<T>(
      url,
      payload,
      {
        headers: this.buildHeaders(headers)
      }
    ).pipe(
      this.handleRequest(config)
    );
  }

  // ======================================================
  // DELETE
  // ======================================================

  delete<T>(
    url: string,
    headers: HeadersType = {},
    config: APIConfig = DEFAULT_CONFIG
  ): Observable<T> {

    return this.http.delete<T>(
      url,
      {
        headers: this.buildHeaders(headers)
      }
    ).pipe(
      this.handleRequest(config)
    );
  }

  // ======================================================
  // MEDIA TOKEN
  // ======================================================

  async getMediaToken(
    fileType: string
  ): Promise<string> {

    if (!this.checkedStorage) {
      this.initFromStorage();
    }

    fileType = fileType
      .split('?')[0]
      .toLowerCase();

    const tokens = this.mediaTokens();

    // ✅ cached token
    if (
      tokens[fileType] &&
      this.isMediaTokenValid(fileType)
    ) {
      return tokens[fileType].token;
    }

    // ✅ fetch token
    const response: any =
      await this.get<any>(
        GET_MEDIA_TOKEN_URL(fileType)
      ).toPromise();

    if (
      response?.code !== ResponseCode.Ok
    ) {
      throw new Error(
        response?.message ??
        'Unable to fetch media token'
      );
    }

    const token =
      response.data.auth_token;

    this.mediaTokens.update(tokens => ({
      ...tokens,
      [fileType]: {
        token,
        expiryTime: null
      }
    }));

    this.tokenSrv.set(
      'MEDIA_TOKEN',
      token,
      fileType
    );

    return token;
  }

  // ======================================================
  // TOKEN VALIDATION
  // ======================================================

  private isMediaTokenValid(
    fileType: string
  ): boolean {

    const token =
      this.mediaTokens()[fileType];

    if (!token) return false;

    // TODO:
    // implement expiry validation
    return true;
  }

  // ======================================================
  // INIT TOKENS FROM STORAGE
  // ======================================================

  private initFromStorage() {

    const tokens:
      Record<string, any> = {};

    Object.keys(sessionStorage)
      .filter(
        key =>
          key.startsWith('MEDIA_TOKEN') &&
          !key.includes('EXPIRATION')
      )
      .forEach(key => {

        const [, fileType] =
          key.split('MEDIA_TOKEN_');

        const token =
          this.tokenSrv.get(
            'MEDIA_TOKEN',
            fileType
          );

        if (!token) return;

        tokens[fileType.toLowerCase()] = {
          token
        };
      });

    this.mediaTokens.set(tokens);

    this.checkedStorage = true;
  }

  // ======================================================
  // TOKEN
  // ======================================================

  getToken(
    key: Name.AUTH_TOKEN
  ): string | null {

    return this.cookieSrv.get(key);
  }

  // ======================================================
  // AUTH HEADER
  // ======================================================

  private getAuthTokenObj() {

    return {
      [Name.AUTH_TOKEN_API]:
        this.getToken(
          Name.AUTH_TOKEN
        ) ?? ''
    };
  }

// ======================================================
// BUILD HEADERS
// ======================================================

private buildHeaders(
  headers: HeadersType = {},
  skipAuthCheck = false
): Record<string, string> {

  const headerObj: Record<string, string> =
    headers instanceof HttpHeaders
      ? headers.keys().reduce<Record<string, string>>(
          (acc, key) => {
            acc[key] = headers.get(key) ?? '';
            return acc;
          },
          {}
        )
      : headers;

  // already has auth header
  if (
    skipAuthCheck &&
    (
      'Authorization' in headerObj ||
      'AuthToken' in headerObj
    )
  ) {
    return headerObj;
  }

  return {
    ...headerObj,
    ...this.getAuthTokenObj()
  };
}

  // ======================================================
  // REQUEST HANDLER
  // ======================================================

  private handleRequest(
    config: APIConfig
  ) {

    const shouldLoad =
      !config.loadInBackground;

    if (shouldLoad) {
      this.loaderSrv.show();
      this.isLoading.set(true);
    }

    return <T>(source: Observable<T>) =>
      source.pipe(

        catchError(err => {

          this.alertSrv
            .defaultErrorMessageHandler(err);

          return throwError(() => err);
        }),

        finalize(() => {

          if (shouldLoad) {
            this.loaderSrv.hide();
            this.isLoading.set(false);
          }
        })
      );
  }

  // ======================================================
  // CLEAR TOKENS
  // ======================================================

  flushMediaTokens() {

    Object.keys(sessionStorage)
      .filter(key =>
        key.startsWith('MEDIA_TOKEN')
      )
      .forEach(key => {
        sessionStorage.removeItem(key);
      });

    this.mediaTokens.set({});
  }
}